import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Camera, X, RotateCcw, RotateCw, Check, ScanLine, Monitor, AlertCircle,
  Wifi, RefreshCw, CheckCircle2, Plus, Settings, Printer, Globe
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

function dataUrlToFile(dataUrl, index = 0) {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8 = new Uint8Array(n);
  while (n--) u8[n] = bstr.charCodeAt(n);
  const ext = mime === 'image/png' ? 'png' : 'jpg';
  return new File([u8], `scan_${Date.now()}_p${index + 1}.${ext}`, { type: mime });
}

const PROTOCOL_META = {
  TWAIN:    { label: 'USB / TWAIN',        badge: 'bg-blue-100 text-blue-700' },
  SANE:     { label: 'USB / SANE',         badge: 'bg-blue-100 text-blue-700' },
  WIA:      { label: 'USB / WIA',          badge: 'bg-blue-100 text-blue-700' },
  BRIDGE:   { label: 'Local Bridge · USB', badge: 'bg-violet-100 text-violet-700' },
  eSCL:     { label: 'WiFi · AirScan',     badge: 'bg-teal-100 text-teal-700' },
  'HTTP-UI':{ label: 'WiFi · Printer',     badge: 'bg-purple-100 text-purple-700' },
  IPP:      { label: 'WiFi · IPP',         badge: 'bg-orange-100 text-orange-700' },
  Unknown:  { label: 'Network Device',     badge: 'bg-gray-100 text-gray-500' },
};

const BRIDGE_WS_URL = 'ws://localhost:9876';

// Open a one-shot WebSocket call and return the first response matching predicate
function bridgeCall(payload, timeoutMs = 15000) {
  return new Promise((resolve, reject) => {
    let ws;
    const timer = setTimeout(() => { ws?.close(); reject(new Error('Bridge timeout')); }, timeoutMs);
    try {
      ws = new WebSocket(BRIDGE_WS_URL);
    } catch (e) { clearTimeout(timer); reject(e); return; }
    ws.onopen = () => ws.send(JSON.stringify(payload));
    ws.onmessage = (e) => {
      clearTimeout(timer);
      ws.close();
      try { resolve(JSON.parse(e.data)); } catch { reject(new Error('Bad JSON from bridge')); }
    };
    ws.onerror = () => { clearTimeout(timer); reject(new Error('Bridge not reachable')); };
    ws.onclose = (ev) => { if (!ev.wasClean) { clearTimeout(timer); reject(new Error('Bridge closed unexpectedly')); } };
  });
}

// Stream scan: open WS, send scan cmd, receive scan_page[] + scan_done (multi-page)
function bridgeScan(payload, onPage, onStatus) {
  return new Promise((resolve, reject) => {
    let ws;
    const pages = [];
    const timer = setTimeout(() => { ws?.close(); reject(new Error('Scan timeout (120s)')); }, 120000);
    try {
      ws = new WebSocket(BRIDGE_WS_URL);
    } catch (e) { clearTimeout(timer); reject(e); return; }
    ws.onopen = () => ws.send(JSON.stringify(payload));
    ws.onmessage = (e) => {
      const msg = JSON.parse(e.data);
      if (msg.error) { clearTimeout(timer); ws.close(); reject(new Error(msg.error)); return; }
      if (msg.action === 'scanning') { onStatus && onStatus(msg.status); return; }
      if (msg.action === 'scan_page') { pages.push(msg.image); onPage && onPage(msg.image, pages.length); return; }
      if (msg.action === 'scan_done' || msg.action === 'scan_result') {
        // scan_result = legacy single-page fallback
        if (msg.image) pages.push(msg.image);
        clearTimeout(timer); ws.close(); resolve(pages);
      }
    };
    ws.onerror = () => { clearTimeout(timer); reject(new Error('Bridge connection failed')); };
  });
}

function blobToDataUrl(blob) {
  return new Promise(resolve => {
    const r = new FileReader();
    r.onloadend = () => resolve(r.result);
    r.readAsDataURL(blob);
  });
}

function ScannerCard({ scanner, selected, onSelect }) {
  const meta = PROTOCOL_META[scanner.protocol] || PROTOCOL_META.Unknown;
  const isUSB = ['TWAIN','SANE','WIA','BRIDGE'].includes(scanner.protocol);
  const Icon = isUSB ? Monitor : scanner.protocol === 'eSCL' ? Wifi : Globe;
  const canScanDirect = ['TWAIN','SANE','WIA','BRIDGE','eSCL'].includes(scanner.protocol);
  const canScanHTTP = (scanner.capabilities || []).includes('scan');
  return (
    <div className={`w-full rounded-xl border-2 transition-all ${
      selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
    }`}>
      <button onClick={() => onSelect(scanner)} className="w-full flex items-center gap-3 p-3 text-left">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${selected ? 'bg-blue-100' : 'bg-gray-100'}`}>
          <Icon className={`w-5 h-5 ${selected ? 'text-blue-600' : 'text-gray-500'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm text-gray-800 truncate">{scanner.model}</div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${meta.badge}`}>{meta.label}</span>
            <span className="text-xs text-gray-400 font-mono">{scanner.host}</span>
            {canScanDirect && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">direct scan ✓</span>}
            {!canScanDirect && canScanHTTP && <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">scan via web UI</span>}
          </div>
        </div>
        {selected && <CheckCircle2 className="w-5 h-5 text-blue-600 shrink-0" />}
      </button>
      {selected && scanner.webUrl && !canScanDirect && (
        <div className="px-3 pb-3">
          <a href={scanner.webUrl} target="_blank" rel="noreferrer"
            className="flex items-center gap-1.5 text-xs text-purple-700 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2 hover:bg-purple-100">
            <Globe className="w-3.5 h-3.5" /> Open scanner web interface → {scanner.webUrl}
          </a>
        </div>
      )}
    </div>
  );
}

export default function ScannerCapture({ onCapture, onClose }) {
  const { authFetch } = useAuth();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [mode, setMode] = useState('camera');
  const [camDevices, setCamDevices] = useState([]);
  const [selectedCam, setSelectedCam] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [captured, setCaptured] = useState(null);
  const [error, setError] = useState('');

  const [scanners, setScanners] = useState([]);
  const [selectedScanner, setSelectedScanner] = useState(null);
  const [discovering, setDiscovering] = useState(false);
  const [discoveryProgress, setDiscoveryProgress] = useState('');
  const [usbStatus, setUsbStatus] = useState(null); // null=checking, {available, message, scanners}
  const [scanning, setScanning] = useState(false);
  const [manualIP, setManualIP] = useState('');
  const [showManual, setShowManual] = useState(false);
  const [resolution, setResolution] = useState(200);
  const [colorMode, setColorMode] = useState('RGB24');
  const [useADF, setUseADF] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [discoverySubnets, setDiscoverySubnets] = useState([]);
  const discoveryAborted = useRef(false);
  const [bridgeStatus, setBridgeStatus] = useState('unchecked'); // unchecked|online|offline
  const [bridgeSystem, setBridgeSystem] = useState('');
  const [capturedPages, setCapturedPages] = useState([]); // multi-page scan results
  const [scanProgress, setScanProgress] = useState(''); // e.g. 'Page 2 scanned…'

  useEffect(() => {
    navigator.mediaDevices?.enumerateDevices().then(devs => {
      const cams = devs.filter(d => d.kind === 'videoinput');
      setCamDevices(cams);
      if (cams.length > 0) setSelectedCam(cams[0].deviceId);
    }).catch(() => {});

    // On HTTP, auto-start network discovery
    if (window.location.protocol !== 'https:') {
      discoverNetwork();
    }

    // 1a. Check Local Scanner Bridge (ws://localhost:9876) — no license needed
    bridgeCall({ action: 'ping' }, 3000)
      .then(pong => {
        setBridgeStatus('online');
        setBridgeSystem(pong.system || '');
        return bridgeCall({ action: 'list' }, 8000);
      })
      .then(resp => {
        if (!resp.devices || resp.devices.length === 0) return;
        const bridgeDevices = resp.devices.map(d => ({
          id: `bridge_${d.id}`,
          model: d.name || d.id,
          protocol: 'BRIDGE',
          bridgeDeviceId: d.id,
          bridgeBackend: d.backend,
          capabilities: ['scan'],
          host: 'localhost'
        }));
        setScanners(prev => [...bridgeDevices, ...prev.filter(p => p.protocol !== 'BRIDGE')]);
        setSelectedScanner(sel => sel || bridgeDevices[0]);
      })
      .catch(() => setBridgeStatus('offline'));

    // 1b. Check DynamSoft TWAIN bridge — port 18622 is the REST API (18625 is settings UI)
    fetch('http://127.0.0.1:18622/DWTAPI/Scanners')
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        if (!Array.isArray(list) || list.length === 0) return;
        const tw = list.map((dev, i) => ({
          id: `twain_${i}`,
          model: (dev.name || dev.Name || `USB Scanner ${i + 1}`).trim(),
          protocol: 'TWAIN',
          twainIndex: i,
          twainDevice: dev.device,
          capabilities: ['scan']
        }));
        setScanners(prev => [...tw, ...prev.filter(p => p.protocol !== 'TWAIN')]);
        setSelectedScanner(sel => sel || tw[0]);
      })
      .catch(() => {});

    // 2. Check SANE USB scanners on the server (Linux backend)
    authFetch('/api/scanner/usb')
      .then(r => r.json())
      .then(data => {
        setUsbStatus(data);
        if (data.scanners && data.scanners.length > 0) {
          setScanners(prev => [
            ...prev.filter(p => p.protocol !== 'SANE'),
            ...data.scanners
          ]);
          setSelectedScanner(sel => sel || data.scanners[0]);
        }
      })
      .catch(() => setUsbStatus({ available: false, scanners: [], message: 'Could not check USB scanners' }));

    return () => stopStream();
  }, [authFetch]);

  // ── Detect client's own LAN IP via WebRTC ─────────────────────────────────
  const getLocalSubnets = () => new Promise((resolve) => {
    const ips = new Set();
    try {
      const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
      pc.createDataChannel('');
      pc.onicecandidate = (e) => {
        if (!e.candidate) {
          pc.close();
          const subnets = [...ips]
            .filter(ip => /^(192\.168|10\.|172\.(1[6-9]|2[0-9]|3[01]))/.test(ip))
            .map(ip => ip.split('.').slice(0, 3).join('.'));
          resolve([...new Set(subnets)]);
          return;
        }
        const m = e.candidate.candidate.match(/([0-9]{1,3}(\.[0-9]{1,3}){3})/);
        if (m) ips.add(m[1]);
      };
      pc.createOffer().then(o => pc.setLocalDescription(o));
      setTimeout(() => { pc.close(); resolve([...new Set([...ips].map(ip => ip.split('.').slice(0,3).join('.')))]); }, 2500);
    } catch { resolve([]); }
  });

  // ── Browser-direct probe: fetch scanner's local IP from the browser itself ──
  // The backend is on a remote VPS and cannot reach office LAN devices.
  // The browser IS on the office LAN, so it can fetch local IPs directly.
  const browserProbeIP = (host, port, path) => new Promise((resolve) => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => { ctrl.abort(); resolve(null); }, 2000);
    fetch(`http://${host}:${port}${path}`, { signal: ctrl.signal, mode: 'no-cors' })
      .then(() => { clearTimeout(timer); resolve({ host, port, reachable: true }); })
      .catch(err => {
        clearTimeout(timer);
        // 'Failed to fetch' with no-cors still means the host responded (CORS blocked but alive)
        // 'AbortError' means timed out (host unreachable)
        const alive = err.name !== 'AbortError';
        resolve(alive ? { host, port, reachable: true } : null);
      });
  });

  // Full eSCL probe — try CORS read first, fall back to no-cors ping
  const browserProbeESCL = async (host) => {
    for (const port of [80, 9095, 8080]) {
      // First try: full CORS read to get model name
      try {
        const ctrl = new AbortController();
        setTimeout(() => ctrl.abort(), 2500);
        const r = await fetch(`http://${host}:${port}/eSCL/ScannerStatus`, { signal: ctrl.signal });
        if (r.ok) {
          const text = await r.text();
          if (text.includes('ScannerStatus') || text.includes('escl') || text.includes('pwg')) {
            const model = text.match(/<pwg:MakeAndModel>(.*?)<\/pwg:MakeAndModel>/)?.[1]
              || text.match(/<scan:MakeAndModel>(.*?)<\/scan:MakeAndModel>/)?.[1]
              || `Scanner at ${host}`;
            return { host, port, model, protocol: 'eSCL', capabilities: ['scan'] };
          }
        }
      } catch {}
      // Second try: no-cors ping — if the path EXISTS the fetch won't abort
      // (AbortError = unreachable, TypeError/opaque = path responded = scanner likely there)
      try {
        const ctrl2 = new AbortController();
        setTimeout(() => ctrl2.abort(), 2000);
        await fetch(`http://${host}:${port}/eSCL/ScannerStatus`, { signal: ctrl2.signal, mode: 'no-cors' });
        // Got an opaque response — eSCL path exists, treat as scanner
        return { host, port, model: `Scanner at ${host}:${port}`, protocol: 'eSCL', capabilities: ['scan'] };
      } catch (e) {
        if (e.name !== 'AbortError') {
          // TypeError = CORS blocked but host responded = scanner is there
          return { host, port, model: `Scanner at ${host}:${port}`, protocol: 'eSCL', capabilities: ['scan'] };
        }
      }
    }
    return null;
  };

  // Try to get model name from printer-specific XML/JSON endpoints
  // These endpoints are served with CORS headers by most printers
  const getDeviceModel = async (host) => {
    const tryFetch = async (url, parse) => {
      try {
        const ctrl = new AbortController();
        setTimeout(() => ctrl.abort(), 2000);
        const r = await fetch(url, { signal: ctrl.signal });
        if (!r.ok) return null;
        const text = await r.text();
        return parse(text);
      } catch { return null; }
    };

    // 1. eSCL ScannerStatus — served with CORS by most modern scanners
    const esclModel = await tryFetch(`http://${host}/eSCL/ScannerStatus`, text =>
      text.match(/<pwg:MakeAndModel>(.*?)<\/pwg:MakeAndModel>/)?.[1] ||
      text.match(/<scan:MakeAndModel>(.*?)<\/scan:MakeAndModel>/)?.[1]);

    if (esclModel) return { model: esclModel, protocol: 'eSCL', canScan: true };

    // 2. HP/Epson device description XML
    const hpModel = await tryFetch(`http://${host}/DevMgmt/ProductConfigDyn.xml`, text =>
      text.match(/<dd:ProductName>(.*?)<\/dd:ProductName>/)?.[1] ||
      text.match(/<psdyn:ProductName>(.*?)<\/psdyn:ProductName>/)?.[1]);
    if (hpModel) return { model: hpModel, protocol: 'HTTP-UI', canScan: true };

    // 3. IPP-over-HTTP attributes (port 631 or 80)
    const ippModel = await tryFetch(`http://${host}:631/`, text =>
      text.match(/<title>(.*?)<\/title>/i)?.[1]?.replace(/&amp;/g, '&').trim());
    if (ippModel) return { model: ippModel, protocol: 'IPP', canScan: false };

    // 4. Brother/Canon/Ricoh status page
    const statusModel = await tryFetch(`http://${host}/general/status.html`, text =>
      text.match(/(?:Model|Device Name)[^>]*>([A-Za-z0-9 _\-]+)</i)?.[1]?.trim());
    if (statusModel) return { model: statusModel, protocol: 'HTTP-UI', canScan: false };

    // 5. Generic: try to resolve hostname via DNS (browsers resolve it)
    // We can attempt a fetch to the IP and check the SSL certificate CN or redirect hostname
    // As fallback, try fetching /hp/device/info (HP) or /PRESENTATION/HTML/TOP/INDEX.HTM (Ricoh)
    const altModel = await tryFetch(`http://${host}/hp/device/info`, text =>
      text.match(/<title>(.*?)<\/title>/i)?.[1]?.replace(/&amp;/g,'&').trim()) ||
      await tryFetch(`http://${host}/PRESENTATION/HTML/TOP/INDEX.HTM`, text =>
        text.match(/<title>(.*?)<\/title>/i)?.[1]?.replace(/&amp;/g,'&').trim()) ||
      await tryFetch(`http://${host}/web/guest/en/websys/webArch/getStatus.cgi`, text =>
        text.match(/<title>(.*?)<\/title>/i)?.[1]?.replace(/&amp;/g,'&').trim());
    if (altModel) return { model: altModel, protocol: 'HTTP-UI', canScan: false };

    return null;
  };

  // Probe port 80 — try model name via CORS-friendly endpoints, show ALL devices
  const browserProbeHTTP = async (host) => {
    const info = await getDeviceModel(host);
    if (info) {
      return {
        host, port: info.protocol === 'IPP' ? 631 : 80,
        model: info.model,
        protocol: info.protocol,
        capabilities: info.canScan ? ['scan'] : [],
        webUrl: `http://${host}/`
      };
    }
    // Device was reachable (phase 1 confirmed) but we can't read its name
    return { host, port: 80, model: `Device at ${host}`, protocol: 'Unknown', capabilities: [], webUrl: `http://${host}/` };
  };

  const discoverNetwork = useCallback(async () => {
    setDiscovering(true);
    setError('');
    discoveryAborted.current = false;

    // Mixed content: HTTPS pages cannot probe local http:// IPs — show clean info, not an error
    if (window.location.protocol === 'https:') {
      setDiscovering(false);
      return;
    }

    setDiscoveryProgress('Detecting your local network…');

    const clientSubnets = await getLocalSubnets();
    setDiscoverySubnets(clientSubnets);

    if (clientSubnets.length === 0) {
      setDiscovering(false);
      setDiscoveryProgress('');
      setError('Could not detect your local network automatically. Enter your scanner IP manually below.');
      return;
    }

    setDiscoveryProgress(`Scanning ${clientSubnets.map(s => s+'.x').join(', ')} from your browser…`);
    const foundMap = new Map();

    // Phase 1: fast no-cors reachability sweep — stop if user picks a device
    const BATCH = 50;
    const reachable = [];
    outer:
    for (const subnet of clientSubnets.slice(0, 2)) {
      for (let i = 1; i <= 254; i += BATCH) {
        if (discoveryAborted.current) break outer;
        const batch = [];
        for (let j = i; j < Math.min(i + BATCH, 255); j++) {
          batch.push(browserProbeIP(`${subnet}.${j}`, 80, '/'));
          batch.push(browserProbeIP(`${subnet}.${j}`, 9095, '/eSCL/ScannerStatus'));
        }
        const results = await Promise.all(batch);
        results.filter(Boolean).forEach(r => reachable.push(r));
        setDiscoveryProgress(`Scanning ${subnet}.${i}–${Math.min(i+BATCH-1,254)} … (${reachable.length} devices found)`);
      }
    }

    if (reachable.length === 0) {
      setDiscovering(false);
      setDiscoveryProgress('');
      setError(`No devices responded on ${clientSubnets.map(s=>s+'.x').join(', ')}. Enter your scanner IP manually.`);
      return;
    }

    // Phase 2: identify all reachable devices
    const uniqueHosts = [...new Set(reachable.map(r => r.host))];
    setDiscoveryProgress(`Found ${uniqueHosts.length} devices, reading device names…`);

    // First: try eSCL directly from browser (scanner-native, works if CORS allowed)
    await Promise.all(uniqueHosts.map(async (host) => {
      if (discoveryAborted.current) return;
      const escl = await browserProbeESCL(host);
      if (escl) {
        const entry = { id: `escl_${host}`, ...escl };
        foundMap.set(host, entry);
        setScanners(prev => [
          ...prev.filter(p => p.protocol === 'TWAIN' || p.protocol === 'SANE'),
          ...foundMap.values()
        ]);
        setSelectedScanner(sel => sel || entry);
      }
    }));

    // Second: try browser-direct model detection on unidentified hosts
    const unidentified = uniqueHosts.filter(h => !foundMap.has(h));
    if (unidentified.length > 0 && !discoveryAborted.current) {
      setDiscoveryProgress(`Identifying ${unidentified.length} devices…`);
      const IBATCH = 6;
      for (let i = 0; i < unidentified.length; i += IBATCH) {
        if (discoveryAborted.current) break;
        await Promise.all(unidentified.slice(i, i + IBATCH).map(async (host) => {
          const http = await browserProbeHTTP(host);
          if (http) {
            foundMap.set(host, { id: `http_${host}`, ...http });
            setScanners(prev => [
              ...prev.filter(p => p.protocol === 'TWAIN' || p.protocol === 'SANE'),
              ...foundMap.values()
            ]);
          }
        }));
        setDiscoveryProgress(`Identified ${Math.min(i + IBATCH, unidentified.length)} / ${unidentified.length} devices…`);
      }
    }

    // Third: anything still not identified — show with just IP (was reachable)
    uniqueHosts.forEach(host => {
      if (!foundMap.has(host)) {
        foundMap.set(host, {
          id: `dev_${host}`, model: `Device at ${host}`,
          host, port: 80, protocol: 'Unknown', capabilities: [], webUrl: `http://${host}/`
        });
      }
    });

    setScanners(prev => [
      ...prev.filter(p => p.protocol === 'TWAIN' || p.protocol === 'SANE'),
      ...foundMap.values()
    ]);
    setDiscoveryProgress('');
    setDiscovering(false);
    const found = [...foundMap.values()];
    const eSCLFirst = found.find(f => f.protocol === 'eSCL');
    setSelectedScanner(sel => sel || eSCLFirst || found[0]);
  }, [authFetch]);

  useEffect(() => {
    if (mode === 'scanner') discoverNetwork();
  }, [mode]);

  const addManualScanner = async () => {
    if (!manualIP.trim()) return;
    const [host, portStr] = manualIP.trim().split(':');
    const port = parseInt(portStr) || 80;
    setDiscovering(true);
    setDiscoveryProgress(`Probing ${host}…`);
    // Try to identify protocol before adding
    const escl = await browserProbeESCL(host);
    const found = escl || await browserProbeHTTP(host)
      || { id: `manual_${host}`, model: `Scanner at ${host}`, host, port, protocol: 'eSCL', capabilities: ['scan'] };
    found.id = `manual_${host}`;
    setScanners(prev => [...prev.filter(p => p.id !== found.id), found]);
    setSelectedScanner(found);
    setShowManual(false);
    setManualIP('');
    setDiscoveryProgress('');
    setDiscovering(false);
  };

  const scanFromScanner = async () => {
    if (!selectedScanner) return;
    setScanning(true);
    setError('');
    setCaptured(null);
    try {
      // HTTP-UI / IPP / Unknown: attempt eSCL scan directly — skip the probe,
      // just try posting a scan job. If it fails we'll show the error with web UI link.
      if (selectedScanner.protocol === 'HTTP-UI' || selectedScanner.protocol === 'IPP' || selectedScanner.protocol === 'Unknown') {
        selectedScanner.protocol = 'eSCL';
        selectedScanner.port = selectedScanner.port || 80;
      }

      if (selectedScanner.protocol === 'BRIDGE') {
        // Local Scanner Bridge — WebSocket to ws://localhost:9876
        setCapturedPages([]);
        const pages = await bridgeScan(
          { action: 'scan', device_id: selectedScanner.bridgeDeviceId, backend: selectedScanner.bridgeBackend,
            resolution: Number(resolution), color_mode: colorMode, use_adf: useADF },
          (dataUrl, n) => { setCapturedPages(p => [...p, dataUrl]); setScanProgress(`Page ${n} scanned…`); },
          () => {}
        );
        setScanProgress('');
        if (pages.length === 0) throw new Error('Bridge returned no pages. Check scanner and document.');
        setCapturedPages(pages);
        setCaptured(pages[0]);
      } else if (selectedScanner.protocol === 'SANE') {
        // SANE USB via server-side scanimage
        const r = await authFetch('/api/scanner/usb-scan', {
          method: 'POST',
          body: JSON.stringify({ saneDevice: selectedScanner.saneDevice, resolution, colorMode })
        });
        if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
        const blob = await r.blob();
        const reader = new FileReader();
        reader.onloadend = () => setCaptured(reader.result);
        reader.readAsDataURL(blob);
      } else if (selectedScanner.protocol === 'TWAIN') {
        const TWAIN = 'http://127.0.0.1:18622'; // REST API port
        const pixelType = colorMode === 'RGB24' ? 2 : colorMode === 'Grayscale8' ? 1 : 0;

        // If we don't have the device string, re-fetch scanner list
        let device = selectedScanner.twainDevice;
        if (!device) {
          const listRes = await fetch(`${TWAIN}/DWTAPI/Scanners`);
          const list = listRes.ok ? await listRes.json() : [];
          const dev = list[selectedScanner.twainIndex || 0];
          if (!dev) throw new Error('Scanner not found. Make sure it is connected and DynamSoft Service is running.');
          device = dev.device; // inner device string
        }

        // Create scan job with license from installed DynamSoft SDK
        const scanParams = {
          license: 't0123WwEAAIbtkeZw0p3QFBoGa9Rx4VWieaHYgEqWOZ+fa20x3bgCSLTGMWYFm3mMg4hYjxvj3TgfvMGVs7Q9ksN7hh8egM+J94IBW2HlD2ipBC6/gHEaTRKNyx5M/8xaNBafPIlPj9VSGZsqIxezNU8JLJ1Hg/gY4HaSYEgF',
          device,
          config: {
            IfShowUI: false,
            PixelType: pixelType,
            Resolution: Number(resolution),
            IfFeederEnabled: useADF,
            IfDuplexEnabled: false
          }
        };

        console.log('[TWAIN] device object:', JSON.stringify(device));
        console.log('[TWAIN] ScanJob params:', JSON.stringify(scanParams));

        const jobRes = await fetch(`${TWAIN}/DWTAPI/ScanJobs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(scanParams)
        });

        if (!jobRes.ok) {
          const txt = await jobRes.text().catch(() => '');
          console.error('[TWAIN] ScanJob error:', jobRes.status, txt);
          // If 400 with license error, retry with UI shown so user can approve
          if (jobRes.status === 400) {
            const jobRes2 = await fetch(`${TWAIN}/DWTAPI/ScanJobs`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ...scanParams, config: { ...scanParams.config, IfShowUI: true } })
            });
            if (!jobRes2.ok) {
              const txt2 = await jobRes2.text().catch(() => '');
              throw new Error(`Scan job failed (${jobRes2.status}): ${txt2 || 'Check scanner is ready and document is loaded'}`);
            }
            const jobId2 = await jobRes2.text();
            const imgRes2 = await fetch(`${TWAIN}/DWTAPI/ScanJobs/${jobId2.trim()}/NextDocument`);
            if (!imgRes2.ok) throw new Error('Could not retrieve scanned image');
            const blob2 = await imgRes2.blob();
            if (!blob2 || blob2.size < 100) throw new Error('Scanner returned empty image');
            const reader2 = new FileReader();
            reader2.onloadend = () => setCaptured(reader2.result);
            reader2.readAsDataURL(blob2);
            return;
          }
          throw new Error(`Scan job failed (${jobRes.status}): ${txt || 'Check scanner is ready and document is loaded'}`);
        }

        const jobId = await jobRes.text(); // returns plain job ID string
        if (!jobId) throw new Error('No scan job ID returned');

        // Fetch all pages via NextDocument until 404
        const twainPages = [];
        for (let p = 0; p < 100; p++) {
          const imgRes = await fetch(`${TWAIN}/DWTAPI/ScanJobs/${jobId.trim()}/NextDocument`);
          if (!imgRes.ok) break; // 404 = no more pages
          const blob = await imgRes.blob();
          if (!blob || blob.size < 100) break;
          const dataUrl = await blobToDataUrl(blob);
          twainPages.push(dataUrl);
          setScanProgress(`Page ${twainPages.length} scanned…`);
        }
        setScanProgress('');
        if (twainPages.length === 0) throw new Error('Scanner returned no pages — check document is loaded and scanner is ready');
        setCapturedPages(twainPages);
        setCaptured(twainPages[0]);
      } else {
        // eSCL: scan directly from browser to scanner on local network
        // Step 1: POST ScanJob
        const scanXml = `<?xml version="1.0" encoding="UTF-8"?>
<scan:ScanSettings xmlns:scan="http://schemas.hp.com/imaging/escl/2011/05/03"
                   xmlns:pwg="http://www.pwg.org/schemas/2010/12/sm">
  <pwg:Version>2.63</pwg:Version>
  <scan:Intent>Document</scan:Intent>
  <scan:InputSource>${useADF ? 'Feeder' : 'Platen'}</scan:InputSource>
  <pwg:ScanRegions><pwg:ScanRegion>
    <pwg:ContentRegionUnits>escl:ThreeHundredthsOfInches</pwg:ContentRegionUnits>
    <pwg:Width>2550</pwg:Width><pwg:Height>3300</pwg:Height>
    <pwg:XOffset>0</pwg:XOffset><pwg:YOffset>0</pwg:YOffset>
  </pwg:ScanRegion></pwg:ScanRegions>
  <scan:ColorMode>${colorMode}</scan:ColorMode>
  <scan:XResolution>${resolution}</scan:XResolution>
  <scan:YResolution>${resolution}</scan:YResolution>
</scan:ScanSettings>`;

        const base = `http://${selectedScanner.host}:${selectedScanner.port || 80}`;
        const webUrl = selectedScanner.webUrl || `http://${selectedScanner.host}/`;

        let jobRes;
        try {
          jobRes = await fetch(`${base}/eSCL/ScanJobs`, {
            method: 'POST',
            headers: { 'Content-Type': 'text/xml' },
            body: scanXml
          });
        } catch {
          // CORS blocked — browser security prevents POST to local IP from remote site
          setScanning(false);
          setError(`CORS_BLOCKED|${selectedScanner.host}|${selectedScanner.port || 80}|${webUrl}`);
          return;
        }

        if (jobRes.status !== 201) {
          throw new Error(`Scanner rejected job (HTTP ${jobRes.status}). Try: ${webUrl}`);
        }

        // Build NextDocument URL from Location header
        const jobLocation = jobRes.headers.get('location');
        let jobBase;
        if (!jobLocation) {
          jobBase = `${base}/eSCL/ScanJobs`;
        } else if (jobLocation.startsWith('http')) {
          jobBase = jobLocation;
        } else {
          // relative path — may or may not start with /
          jobBase = `${base}${jobLocation.startsWith('/') ? '' : '/'}${jobLocation}`;
        }
        // Some scanners include /NextDocument in Location, others don't
        const docUrl = jobBase.endsWith('/NextDocument') ? jobBase : `${jobBase}/NextDocument`;

        // Step 2: collect all pages — poll NextDocument until 404 or timeout
        const esclPages = [];
        let consecutiveEmpty = 0;
        pageLoop:
        for (let p = 0; p < 100; p++) {
          // Poll for next page ready — up to 30s per page
          let blob = null;
          for (let i = 0; i < 30; i++) {
            await new Promise(r => setTimeout(r, 2000));
            try {
              const imgRes = await fetch(docUrl);
              if (imgRes.status === 404) { break pageLoop; } // no more pages
              if (imgRes.ok) {
                const ct = imgRes.headers.get('content-type') || '';
                if (ct.startsWith('image/') || ct === 'application/octet-stream') {
                  const b = await imgRes.blob();
                  if (b.size > 500) { blob = b; break; }
                }
              }
              // 503 = scanner still processing — keep polling
            } catch {}
          }
          if (!blob) { consecutiveEmpty++; if (consecutiveEmpty >= 2) break; continue; }
          consecutiveEmpty = 0;
          const dataUrl = await blobToDataUrl(blob);
          esclPages.push(dataUrl);
          setScanProgress(`Page ${esclPages.length} scanned…`);
          // After getting a page, immediately try next (ADF feeds continuously)
          // Short delay for scanner to prepare next page
          await new Promise(r => setTimeout(r, 500));
        }
        setScanProgress('');
        if (esclPages.length === 0) throw new Error('Scanner did not return an image. Make sure the document is placed on the scanner and try again.');
        setCapturedPages(esclPages);
        setCaptured(esclPages[0]);
      }
    } catch (err) {
      setError('Scan failed: ' + err.message);
    } finally {
      setScanning(false);
    }
  };

  const stopStream = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setStreaming(false);
  };

  const startCamera = useCallback(async () => {
    stopStream(); setError(''); setCaptured(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: selectedCam ? { exact: selectedCam } : undefined, width: { ideal: 1920 }, height: { ideal: 1080 } }
      });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); }
      setStreaming(true);
    } catch (err) { setError('Camera error: ' + err.message); }
  }, [selectedCam]);

  const captureFrame = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current, c = canvasRef.current;
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext('2d').drawImage(v, 0, 0);
    setCaptured(c.toDataURL('image/jpeg', 0.95));
    stopStream();
  };

  const confirmCapture = () => {
    const pages = capturedPages.length > 0 ? capturedPages : (captured ? [captured] : []);
    if (pages.length === 0) return;
    onCapture(pages.map((du, i) => dataUrlToFile(du, i)));
    onClose();
  };

  const rotateCaptured = (deg) => {
    if (!captured) return;
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const swap = deg % 180 !== 0;
      canvas.width  = swap ? img.height : img.width;
      canvas.height = swap ? img.width  : img.height;
      const ctx = canvas.getContext('2d');
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((deg * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      setCaptured(canvas.toDataURL('image/jpeg', 0.95));
    };
    img.src = captured;
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col" style={{ maxHeight: '95vh' }}>

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center">
              <ScanLine className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-800">Capture Document</h2>
              <p className="text-xs text-gray-400">Webcam, USB scanner, or WiFi network scanner</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 p-1 mx-4 mt-4 rounded-xl w-fit shrink-0">
          <button onClick={() => setMode('guide')}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${mode === 'guide' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500'}`}>
            <AlertCircle className="w-4 h-4" /> User Guide
          </button>
          <button onClick={() => setMode('camera')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${mode === 'camera' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500'}`}>
            <Camera className="w-4 h-4" /> Camera
          </button>
          <button onClick={() => setMode('scanner')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${mode === 'scanner' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500'}`}>
            <ScanLine className="w-4 h-4" /> Scanner
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">

          {/* USER GUIDE TAB */}
          {mode === 'guide' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                <p className="text-sm font-semibold text-blue-800">How to scan documents</p>
                <div className="space-y-2 text-xs text-blue-700">
                  <p><strong>USB Scanner:</strong> Connect scanner via USB cable, install <a href="https://www.dynamsoft.com/web-twain/downloads/" target="_blank" rel="noreferrer" className="underline">DynamSoft Service</a>, then refresh this page. Your scanner will appear under USB/Wired.</p>
                  <p><strong>WiFi Scanner:</strong> Open the app over <strong>HTTP</strong> (not HTTPS) for auto-discovery to work. Or enter your scanner's IP manually using the "Add scanner by IP" button in the Scanner tab.</p>
                  <p><strong>Camera:</strong> Use the Camera tab to take a photo of your document using your device's camera.</p>
                </div>
              </div>

              {window.location.protocol === 'https:' && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
                  <p className="text-xs font-semibold text-amber-900">WiFi auto-discovery blocked by HTTPS</p>
                  <p className="text-xs text-amber-700">Browser security prevents probing local IPs from an HTTPS page.</p>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <button onClick={() => { setMode('scanner'); setShowManual(true); }}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium">
                      Add scanner IP manually →
                    </button>
                    <a href={`http://${window.location.host}${window.location.pathname}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium">
                      <Globe className="w-3.5 h-3.5" /> Switch to HTTP (enables WiFi discovery)
                    </a>
                  </div>
                </div>
              )}

              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-2">
                <p className="text-xs font-semibold text-gray-700">DynamSoft Service (USB scanning)</p>
                <p className="text-xs text-gray-500">Option A — <strong>Local Bridge (recommended, free):</strong> Run the included Python script on this computer. No license needed.</p>
                <div className="bg-gray-100 rounded-lg px-3 py-2 font-mono text-xs text-gray-700 space-y-1">
                  <p># Windows: double-click</p>
                  <p>scanner-bridge/start_bridge.bat</p>
                  <p className="pt-1"># Linux / macOS:</p>
                  <p>bash scanner-bridge/start_bridge.sh</p>
                </div>
                <p className="text-xs text-gray-500 pt-1">Option B — DynamSoft Service (requires license): <a href="https://www.dynamsoft.com/web-twain/downloads/" target="_blank" rel="noreferrer" className="text-blue-600 underline">Download →</a></p>
              </div>
            </div>
          )}

          {mode === 'scanner' && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-medium ${
              bridgeStatus === 'online'
                ? 'bg-violet-50 border-violet-200 text-violet-700'
                : bridgeStatus === 'offline'
                  ? 'bg-gray-50 border-gray-200 text-gray-400'
                  : 'bg-gray-50 border-gray-200 text-gray-400'
            }`}>
              <span className={`w-2 h-2 rounded-full shrink-0 ${
                bridgeStatus === 'online' ? 'bg-violet-500 animate-pulse' : 'bg-gray-300'
              }`} />
              {bridgeStatus === 'online'
                ? `Local Bridge online · ${bridgeSystem}`
                : 'Local Bridge offline — start scanner-bridge/start_bridge to enable USB scanning'}
              {bridgeStatus === 'offline' && (
                <button onClick={() => {
                  setBridgeStatus('unchecked');
                  bridgeCall({ action: 'ping' }, 3000)
                    .then(pong => { setBridgeStatus('online'); setBridgeSystem(pong.system || ''); return bridgeCall({ action: 'list' }, 8000); })
                    .then(resp => {
                      if (!resp.devices?.length) return;
                      const bd = resp.devices.map(d => ({ id: `bridge_${d.id}`, model: d.name || d.id, protocol: 'BRIDGE', bridgeDeviceId: d.id, bridgeBackend: d.backend, capabilities: ['scan'], host: 'localhost' }));
                      setScanners(prev => [...bd, ...prev.filter(p => p.protocol !== 'BRIDGE')]);
                      setSelectedScanner(sel => sel || bd[0]);
                    })
                    .catch(() => setBridgeStatus('offline'));
                }} className="ml-auto text-blue-600 hover:text-blue-700 underline">Retry</button>
              )}
            </div>
          )}

          {window.location.protocol === 'https:' && mode === 'scanner' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-amber-900">WiFi auto-discovery unavailable on HTTPS</p>
                  <p className="text-xs text-amber-700 mt-0.5">Browser security blocks probing local network IPs from an HTTPS page. USB (DynamSoft) scanners still work.</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setShowManual(true)}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium">
                  Add scanner IP manually
                </button>
                <a href={`http://${window.location.host}${window.location.pathname}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium">
                  <Globe className="w-3.5 h-3.5" /> Switch to HTTP (enables WiFi discovery)
                </a>
              </div>
            </div>
          )}

          {error && error.startsWith('CORS_BLOCKED|') ? (() => {
            const [, host, port, webUrl] = error.split('|');
            return (
              <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-800">Browser security (CORS) blocked direct scanning</p>
                    <p className="text-xs text-amber-700 mt-1">
                      Your scanner at <strong>{host}</strong> was found but the browser cannot send scan commands
                      to it from a remotely-hosted website due to CORS security policy.
                    </p>
                  </div>
                  <button onClick={() => setError('')}><X className="w-4 h-4 text-amber-500" /></button>
                </div>
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-amber-800">Option 1 — Use scanner's web interface:</p>
                  <a href={`http://${host}:${port}`} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 text-xs bg-white border border-amber-300 rounded-lg px-3 py-2 text-blue-700 hover:bg-blue-50 font-medium">
                    <Globe className="w-4 h-4 shrink-0" />
                    Open scanner web interface → http://{host}:{port}
                  </a>
                  <p className="text-xs text-amber-700">Scan from there, save the file, then upload it below.</p>
                  <label className="flex items-center gap-2 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-2 cursor-pointer font-medium w-fit">
                    <ScanLine className="w-4 h-4" /> Upload scanned file
                    <input type="file" accept="image/*,.pdf" className="hidden"
                      onChange={e => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        const reader = new FileReader();
                        reader.onloadend = () => { setCaptured(reader.result); setError(''); };
                        reader.readAsDataURL(f);
                      }} />
                  </label>
                </div>
                <div className="border-t border-amber-200 pt-2">
                  <p className="text-xs font-semibold text-amber-800">Option 2 — Run local scanner agent on your laptop:</p>
                  <code className="block text-xs bg-white border border-amber-200 rounded px-3 py-2 mt-1 text-gray-700 select-all">
                    node /path/to/scanner-agent/index.js
                  </code>
                  <p className="text-xs text-amber-600 mt-1">This runs a tiny local proxy that lets the browser reach your scanner.</p>
                </div>
              </div>
            );
          })() : error ? (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span className="flex-1">{error}</span>
              <button onClick={() => setError('')}><X className="w-4 h-4" /></button>
            </div>
          ) : null}

          {/* CAMERA */}
          {mode === 'camera' && (
            <div className="space-y-4">
              {camDevices.length > 1 && (
                <select value={selectedCam} onChange={e => setSelectedCam(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  {camDevices.map(d => (
                    <option key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${d.deviceId.slice(0, 8)}`}</option>
                  ))}
                </select>
              )}
              <div className="bg-black rounded-xl overflow-hidden min-h-[280px] flex items-center justify-center relative">
                {!streaming && !captured && (
                  <div className="flex flex-col items-center gap-3 text-white/40">
                    <Camera className="w-14 h-14" />
                    <p className="text-sm">Click Start Camera to begin</p>
                  </div>
                )}
                <video ref={videoRef} className={`w-full ${streaming ? '' : 'hidden'}`} playsInline muted autoPlay />
                {captured && <img src={captured} alt="Captured" className="w-full" />}
                {captured && (
                  <div className="absolute top-2 left-2 flex gap-2">
                    <button onClick={() => rotateCaptured(-90)} title="Rotate CCW"
                      className="bg-black/60 hover:bg-black/80 text-white p-1.5 rounded-full backdrop-blur-sm">
                      <RotateCcw className="w-4 h-4" />
                    </button>
                    <button onClick={() => rotateCaptured(90)} title="Rotate CW"
                      className="bg-black/60 hover:bg-black/80 text-white p-1.5 rounded-full backdrop-blur-sm">
                      <RotateCw className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              <canvas ref={canvasRef} className="hidden" />
              <div className="flex gap-3">
                {!streaming && !captured && (
                  <button onClick={startCamera}
                    className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium">
                    <Camera className="w-4 h-4" /> Start Camera
                  </button>
                )}
                {streaming && (
                  <button onClick={captureFrame}
                    className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium">
                    <Check className="w-4 h-4" /> Capture
                  </button>
                )}
                {captured && (<>
                  <button onClick={() => { setCaptured(null); startCamera(); }}
                    className="flex-1 flex items-center justify-center gap-2 border border-gray-300 text-gray-700 px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-50">
                    <RotateCcw className="w-4 h-4" /> Retake
                  </button>
                  <button onClick={confirmCapture}
                    className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium">
                    <Check className="w-4 h-4" /> Use This Image
                  </button>
                </>)}
              </div>
            </div>
          )}

          {/* SCANNER */}
          {mode === 'scanner' && (
            <div className="space-y-4">

              {/* Toolbar */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-700">Available Scanners</p>
                  {discoverySubnets.length > 0 && (
                    <p className="text-xs text-gray-400 mt-0.5">Searched: {discoverySubnets.map(s => s + '.x').join(', ')}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={discoverNetwork} disabled={discovering}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                    <RefreshCw className={`w-3.5 h-3.5 ${discovering ? 'animate-spin' : ''}`} />
                    {discovering ? 'Scanning…' : 'Refresh'}
                  </button>
                  <button onClick={() => setShowSettings(s => !s)}
                    className={`flex items-center gap-1.5 text-xs px-3 py-1.5 border rounded-lg transition-colors ${
                      showSettings ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}>
                    <Settings className="w-3.5 h-3.5" /> Settings
                  </button>
                </div>
              </div>

              {/* Settings panel */}
              {showSettings && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Resolution (DPI)</label>
                    <select value={resolution} onChange={e => setResolution(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                      <option value={100}>100 — Draft</option>
                      <option value={200}>200 — Standard</option>
                      <option value={300}>300 — High Quality</option>
                      <option value={600}>600 — Archive</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Color Mode</label>
                    <select value={colorMode} onChange={e => setColorMode(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                      <option value="RGB24">Color</option>
                      <option value="Grayscale8">Grayscale</option>
                      <option value="BlackAndWhite1">Black &amp; White</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-gray-500 block mb-2">Paper Source</label>
                    <div className="flex gap-2">
                      <button onClick={() => setUseADF(false)}
                        className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-colors ${
                          !useADF ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                        }`}>
                        Flatbed (glass)
                      </button>
                      <button onClick={() => setUseADF(true)}
                        className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-colors ${
                          useADF ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                        }`}>
                        Auto Feed (ADF tray)
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── USB / Wired section ── */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Monitor className="w-3.5 h-3.5 text-gray-400" />
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">USB / Wired</p>
                </div>
                {scanners.filter(s => ['TWAIN','SANE','WIA','BRIDGE'].includes(s.protocol)).length > 0 ? (
                  <div className="space-y-2">
                    {scanners.filter(s => ['TWAIN','SANE','WIA','BRIDGE'].includes(s.protocol)).map(s => (
                      <ScannerCard key={s.id} scanner={s} selected={selectedScanner?.id === s.id} onSelect={sc => { discoveryAborted.current = true; setSelectedScanner(sc); setDiscovering(false); setDiscoveryProgress(''); }} />
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-xs text-gray-600 space-y-2">
                    {usbStatus === null
                      ? <p className="flex items-center gap-1.5"><span className="w-3 h-3 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin" /> Checking for USB scanners…</p>
                      : <>
                          <p className="font-semibold text-gray-700">No USB scanner detected</p>
                          <p><strong>Option 1 (recommended, free):</strong> Run the Local Bridge on this computer:</p>
                          <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 font-mono text-gray-700 space-y-0.5">
                            <p className="text-gray-400"># Windows:</p>
                            <p>scanner-bridge\start_bridge.bat</p>
                            <p className="text-gray-400 pt-1"># Linux / macOS:</p>
                            <p>bash scanner-bridge/start_bridge.sh</p>
                          </div>
                          <p className="text-gray-500">Then click <strong>Refresh</strong> — your USB scanner will appear here.</p>
                          <p className="pt-1"><strong>Option 2:</strong> <a href="https://www.dynamsoft.com/web-twain/downloads/" target="_blank" rel="noreferrer" className="text-blue-600 underline">DynamSoft Service (requires license) →</a></p>
                        </>
                    }
                  </div>
                )}
              </div>

              {/* ── WiFi / Network section ── */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Wifi className="w-3.5 h-3.5 text-gray-400" />
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">WiFi / Network</p>
                </div>
                {(() => {
                  const wifiScanners = scanners.filter(s => !['TWAIN','SANE','WIA','BRIDGE'].includes(s.protocol));
                  if (discovering && wifiScanners.length === 0) return (
                    <div className="flex items-center gap-3 py-4 text-gray-400 text-sm">
                      <span className="w-5 h-5 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin shrink-0" />
                      {discoveryProgress || 'Scanning your network…'}
                    </div>
                  );
                  if (wifiScanners.length === 0 && !discovering) return (
                    <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-xs text-gray-500">
                      No WiFi/network scanners found. Make sure your scanner is powered on and on the same network.
                    </div>
                  );
                  return (
                    <div className="space-y-2">
                      {wifiScanners.map(s => (
                        <ScannerCard key={s.id} scanner={s} selected={selectedScanner?.id === s.id} onSelect={sc => { discoveryAborted.current = true; setSelectedScanner(sc); setDiscovering(false); setDiscoveryProgress(''); }} />
                      ))}
                      {discovering && (
                        <div className="flex items-center gap-2 text-xs text-gray-400 px-1">
                          <span className="w-3.5 h-3.5 border-2 border-gray-200 border-t-blue-400 rounded-full animate-spin shrink-0" />
                          {discoveryProgress}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Manual IP entry */}
              <div>
                {!showManual ? (
                  <button onClick={() => setShowManual(true)}
                    className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 font-medium">
                    <Plus className="w-3.5 h-3.5" /> Add scanner by IP address
                  </button>
                ) : (
                  <div className="flex gap-2 items-center bg-gray-50 border border-gray-200 rounded-xl p-3">
                    <input value={manualIP} onChange={e => setManualIP(e.target.value)}
                      placeholder="192.168.1.50  or  192.168.1.50:9095"
                      onKeyDown={e => e.key === 'Enter' && addManualScanner()}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    <button onClick={addManualScanner}
                      className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">Add</button>
                    <button onClick={() => { setShowManual(false); setManualIP(''); }}>
                      <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                    </button>
                  </div>
                )}
              </div>

              {/* Scan progress */}
              {scanning && scanProgress && (
                <div className="flex items-center gap-2 text-sm text-blue-600 font-medium">
                  <span className="w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin shrink-0" />
                  {scanProgress}
                </div>
              )}

              {/* Scanned preview — multi-page thumbnails or single */}
              {capturedPages.length > 1 ? (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-gray-500">{capturedPages.length} pages scanned</p>
                  <div className="grid grid-cols-3 gap-2">
                    {capturedPages.map((pg, i) => (
                      <div key={i} className="relative rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                        <img src={pg} alt={`Page ${i + 1}`} className="w-full object-cover" />
                        <span className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded font-medium">{i + 1}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : captured ? (
                <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm relative">
                  <img src={captured} alt="Scanned" className="w-full" />
                  <div className="absolute top-2 left-2 flex gap-2">
                    <button onClick={() => rotateCaptured(-90)} title="Rotate CCW"
                      className="bg-black/60 hover:bg-black/80 text-white p-1.5 rounded-full backdrop-blur-sm">
                      <RotateCcw className="w-4 h-4" />
                    </button>
                    <button onClick={() => rotateCaptured(90)} title="Rotate CW"
                      className="bg-black/60 hover:bg-black/80 text-white p-1.5 rounded-full backdrop-blur-sm">
                      <RotateCw className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : null}

              {/* Scan buttons */}
              <div className="flex gap-3">
                <button
                  onClick={selectedScanner ? scanFromScanner : undefined}
                  disabled={scanning || discovering}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    scanning || discovering
                      ? 'bg-blue-400 cursor-wait text-white'
                      : selectedScanner
                        ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}>
                  {scanning
                    ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Scanning…</>
                    : discovering
                      ? <><span className="w-4 h-4 border-2 border-white/30 border-t-blue-200 rounded-full animate-spin" /> Searching…</>
                      : !selectedScanner
                        ? <><ScanLine className="w-4 h-4" /> Select a Scanner First</>
                        : <><ScanLine className="w-4 h-4" /> Scan Document</>}
                </button>
                {(captured || capturedPages.length > 0) && (
                  <button onClick={confirmCapture}
                    className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium">
                    <Check className="w-4 h-4" />
                    {capturedPages.length > 1 ? `Use ${capturedPages.length} Pages` : 'Use Scanned Image'}
                  </button>
                )}
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
}
